import { CombinedGraphQLErrors, ServerError } from '@apollo/client';
import type { GraphQLFormattedError } from 'graphql';
import { GraphQLErrorCode } from '@insio/validation';

const AUTHENTICATION_REQUIRED_MESSAGE = 'Authentication required';
const HTTP_UNAUTHORIZED = 401;

/** True for one GraphQL error that says the session is missing or expired. */
export function isUnauthenticatedGraphQLError(error: GraphQLFormattedError): boolean {
  return (
    error.extensions?.code === GraphQLErrorCode.UNAUTHENTICATED ||
    error.message === AUTHENTICATION_REQUIRED_MESSAGE
  );
}

/**
 * True when the server answered that the caller is signed out — the only error that ends a
 * session. Rate limits, 5xx and network failures are never one.
 */
export function isUnauthenticatedError(error: unknown): boolean {
  if (CombinedGraphQLErrors.is(error)) {
    return error.errors.some(isUnauthenticatedGraphQLError);
  }
  return ServerError.is(error) && error.statusCode === HTTP_UNAUTHORIZED;
}

/** A refusal saying the session acts in the other context axis — never a sign-out. */
export type ContextRequiredCode =
  | typeof GraphQLErrorCode.ACCOUNT_CONTEXT_REQUIRED
  | typeof GraphQLErrorCode.MASTER_ACCOUNT_CONTEXT_REQUIRED;

const HTTP_FORBIDDEN = 403;

function asContextRequiredCode(code: unknown): ContextRequiredCode | null {
  return code === GraphQLErrorCode.ACCOUNT_CONTEXT_REQUIRED ||
    code === GraphQLErrorCode.MASTER_ACCOUNT_CONTEXT_REQUIRED
    ? code
    : null;
}

/** Codes in an error body: top-level `code` (middleware) and `errors[].extensions.code` (GraphQL). */
export function codesInBody(bodyText: string): unknown[] {
  try {
    const body = JSON.parse(bodyText) as { code?: unknown; errors?: unknown } | null;
    const errors = Array.isArray(body?.errors) ? body.errors : [];
    return [
      body?.code,
      ...errors.map(e => (e as { extensions?: { code?: unknown } } | null)?.extensions?.code)
    ];
  } catch {
    return [];
  }
}

/**
 * The context the server asked for when it refused an operation because the session acts in a
 * master account (`ACCOUNT_CONTEXT_REQUIRED`) or in an account (`MASTER_ACCOUNT_CONTEXT_REQUIRED`).
 */
export function contextRequiredCode(error: unknown): ContextRequiredCode | null {
  const codes = CombinedGraphQLErrors.is(error)
    ? error.errors.map(e => e.extensions?.code)
    : ServerError.is(error) && error.statusCode === HTTP_FORBIDDEN
      ? codesInBody(error.bodyText)
      : [];
  for (const code of codes) {
    const contextCode = asContextRequiredCode(code);
    if (contextCode) return contextCode;
  }
  return null;
}
